// Ported from ops-portal/src/app/pages/manage-businesses/manage-businesses.component.
// Route: /manage-businesses (index)
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBusinessEntitiesQuery } from './api';
import type { BusinessEntityListItem } from '@/types/business-entity';

type StatusFilter = 'all' | 'active' | 'freezed';
type BusinessTypeFilter = 'all' | 'default' | 'cashpoint';
type PricingPlanFilter = 'all' | 'starter' | 'pro' | 'business' | 'vip';
type PlanFrequencyFilter = 'all' | 'free' | 'monthly' | 'yearly';

interface Filters {
  status: StatusFilter;
  businessType: BusinessTypeFilter;
  pricingPlan: PricingPlanFilter;
  planFrequency: PlanFrequencyFilter;
}

function applyFilters(items: BusinessEntityListItem[], filters: Filters): BusinessEntityListItem[] {
  let result = items;
  if (filters.status !== 'all') {
    result = result.filter((be) => (filters.status === 'freezed' ? be.isFreezed : !be.isFreezed));
  }
  if (filters.businessType !== 'all') {
    result = result.filter((be) => be.businessType === filters.businessType);
  }
  if (filters.pricingPlan !== 'all') {
    result = result.filter((be) => be.subscriptionPlan === filters.pricingPlan);
  }
  if (filters.planFrequency !== 'all') {
    result = result.filter((be) => be.planFrequency === filters.planFrequency);
  }
  return result;
}

const DEFAULT_FILTERS: Filters = {
  status: 'all',
  businessType: 'all',
  pricingPlan: 'all',
  planFrequency: 'all',
};

export function ManageBusinessesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useBusinessEntitiesQuery();

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const patch = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // Mirror Angular behaviour: cashpoint forces plan/frequency to 'all'.
      if (key === 'businessType' && value === 'cashpoint') {
        next.pricingPlan = 'all';
        next.planFrequency = 'all';
      }
      return next;
    });
    setPage(0);
  };

  const filtered = useMemo(() => {
    const items = query.data ?? [];
    let result = applyFilters(items, filters);
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter((be) => be.companyName.toLowerCase().includes(term));
    }
    return result;
  }, [query.data, filters, search]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('pages.manage_businesses.title', { defaultValue: 'Manage Businesses' })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('pages.manage_businesses.subtitle', { defaultValue: '' })}
      </Typography>

      <Paper sx={{ p: 2 }}>
        {/* ── Filters ── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          {/* Status */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>
              {t('pages.manage_businesses.filter.be_status_filter.title', { defaultValue: 'Status' })}
            </InputLabel>
            <Select
              value={filters.status}
              label={t('pages.manage_businesses.filter.be_status_filter.title', { defaultValue: 'Status' })}
              onChange={(e) => patch('status', e.target.value as StatusFilter)}
            >
              <MenuItem value="all">
                {t('pages.manage_businesses.filter.be_status_filter.all', { defaultValue: 'All' })}
              </MenuItem>
              <MenuItem value="active">
                {t('pages.manage_businesses.be_status.active', { defaultValue: 'Active' })}
              </MenuItem>
              <MenuItem value="freezed">
                {t('pages.manage_businesses.be_status.freezed', { defaultValue: 'Freezed' })}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Business type */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>
              {t('pages.manage_businesses.filter.be_business_type_filter.title', { defaultValue: 'Type' })}
            </InputLabel>
            <Select
              value={filters.businessType}
              label={t('pages.manage_businesses.filter.be_business_type_filter.title', { defaultValue: 'Type' })}
              onChange={(e) => patch('businessType', e.target.value as BusinessTypeFilter)}
            >
              <MenuItem value="all">
                {t('pages.manage_businesses.filter.be_business_type_filter.all', { defaultValue: 'All' })}
              </MenuItem>
              <MenuItem value="default">
                {t('pages.manage_businesses.be_business_types.default', { defaultValue: 'Default' })}
              </MenuItem>
              <MenuItem value="cashpoint">
                {t('pages.manage_businesses.be_business_types.cashpoint', { defaultValue: 'Cashpoint' })}
              </MenuItem>
            </Select>
          </FormControl>

          {/* Plan + frequency — hidden for cashpoint */}
          {filters.businessType !== 'cashpoint' && (
            <>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>
                  {t('pages.manage_businesses.filter.be_pricing_plan_filter.title', { defaultValue: 'Plan' })}
                </InputLabel>
                <Select
                  value={filters.pricingPlan}
                  label={t('pages.manage_businesses.filter.be_pricing_plan_filter.title', { defaultValue: 'Plan' })}
                  onChange={(e) => patch('pricingPlan', e.target.value as PricingPlanFilter)}
                >
                  <MenuItem value="all">
                    {t('pages.manage_businesses.filter.be_pricing_plan_filter.all', { defaultValue: 'All' })}
                  </MenuItem>
                  {(['starter', 'pro', 'business', 'vip'] as const).map((plan) => (
                    <MenuItem key={plan} value={plan}>
                      {t(`pages.manage_businesses.be_pricing_plans.${plan}`, { defaultValue: plan })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>
                  {t('pages.manage_businesses.filter.be_pricing_plan_frequency_filter.title', {
                    defaultValue: 'Frequency',
                  })}
                </InputLabel>
                <Select
                  value={filters.planFrequency}
                  label={t('pages.manage_businesses.filter.be_pricing_plan_frequency_filter.title', {
                    defaultValue: 'Frequency',
                  })}
                  onChange={(e) => patch('planFrequency', e.target.value as PlanFrequencyFilter)}
                >
                  <MenuItem value="all">
                    {t('pages.manage_businesses.filter.be_pricing_plan_frequency_filter.all', { defaultValue: 'All' })}
                  </MenuItem>
                  {(['free', 'monthly', 'yearly'] as const).map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {t(`pages.manage_businesses.be_pricing_plan_frequencies.${freq}`, { defaultValue: freq })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {/* Search */}
          <TextField
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder={t('pages.manage_businesses.table.search_be', { defaultValue: 'Search…' })}
            sx={{ minWidth: 220, ml: 'auto' }}
          />
        </Stack>

        {/* ── Table ── */}
        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('pages.manage_businesses.table.businessEntity', { defaultValue: 'Business Entity' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.manage_businesses.table.status', { defaultValue: 'Status' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.manage_businesses.table.businessType', { defaultValue: 'Type' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.manage_businesses.table.pricingPlan', { defaultValue: 'Plan' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.manage_businesses.table.frequency', { defaultValue: 'Frequency' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.manage_businesses.actions', { defaultValue: 'Actions' })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((be) => (
                    <TableRow key={be.id} hover>
                      <TableCell>{be.companyName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(
                            `pages.manage_businesses.be_status.${be.isFreezed ? 'freezed' : 'active'}`,
                            { defaultValue: be.isFreezed ? 'Freezed' : 'Active' },
                          )}
                          color={be.isFreezed ? 'error' : 'success'}
                        />
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{be.businessType}</TableCell>
                      <TableCell>
                        {be.businessType === 'default'
                          ? t(`pages.manage_businesses.be_pricing_plans.${be.subscriptionPlan}`, {
                              defaultValue: be.subscriptionPlan,
                            })
                          : '–'}
                      </TableCell>
                      <TableCell>
                        {be.businessType === 'default'
                          ? t(`pages.manage_businesses.be_pricing_plan_frequencies.${be.planFrequency}`, {
                              defaultValue: be.planFrequency,
                            })
                          : '–'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/manage-businesses/be/${be.id}`)}
                          >
                            {t('global.view_details', { defaultValue: 'View details' })}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/pricing/be/details/${be.id}`)}
                          >
                            {t('global.view_pricing', { defaultValue: 'View pricing' })}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('messages.no-data', { defaultValue: 'No data' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              labelDisplayedRows={({ from, to, count }) =>
                t('pages.userOperations.table.pagination.range', {
                  defaultValue: `${from}–${to} of ${count}`,
                  from,
                  to,
                  count,
                })
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
